import { UtilActionType } from "models/actionTypes/utilActionTypes";
import { ReduxConstants } from "utils/constants";
import { MessageActionProps } from "models/propTypes/messagePropTypes";
import { DialogProps } from "models/propTypes/dialogPropTypes";
import { StyleEnums } from "models/enums";
import { SnackbarKey } from "notistack";

export const loadingStart = (): UtilActionType => {
  return {
    type: ReduxConstants.UtilActions.LOADING_START,
  };
};

export const loadingEnd = (): UtilActionType => {
  return {
    type: ReduxConstants.UtilActions.LOADING_END,
  };
};

const displayMessage = (message: MessageActionProps): UtilActionType => {
  return {
    type: ReduxConstants.UtilActions.DISPLAY_MESSAGE,
    payload: message,
  };
};

/**
 * Displays an error message.
 * @param text - The text of the message.
 * @param options - The options for the message.
 * @param options.isGlobal - Whether the message should be added to the global message list. As an example, when we are in a inner message provider like for todos and want to display a message in the global message list.
 * @param options.showSupportLink - Whether to render the "Kontakta oss" support-case link after the text.
 * @returns The action to display the message.
 */
export const displayErrorMessage = (
  text: string,
  options?: { isGlobal?: boolean; showSupportLink?: boolean }
): UtilActionType => {
  return displayMessage({
    text: text,
    type: StyleEnums.DisplayType.Danger,
    isGlobal: options?.isGlobal,
    showSupportLink: options?.showSupportLink,
  });
};

/**
 * Displays a success message.
 * @param text - The text of the message.
 * @param options - The options for the message.
 * @param options.isGlobal - Whether the message should be added to the global message list. As an example, when we are in a inner message provider like for todos and want to display a message in the global message list.
 * @returns The action to display the message.
 */
export const displaySuccessMessage = (text: string, options?: { isGlobal?: boolean }): UtilActionType => {
  return displayMessage({ text: text, type: StyleEnums.DisplayType.Success, isGlobal: options?.isGlobal });
};

/**
 * Displays an info message.
 * @param text - The text of the message.
 * @param options - The options for the message.
 * @param options.isGlobal - Whether the message should be added to the global message list. As an example, when we are in a inner message provider like for todos and want to display a message in the global message list.
 * @returns The action to display the message.
 */
export const displayInfoMessage = (text: string, options?: { isGlobal?: boolean }): UtilActionType => {
  return displayMessage({ text: text, type: StyleEnums.DisplayType.Info, isGlobal: options?.isGlobal });
};

/**
 * Displays a warning message.
 * @param text - The text of the message.
 * @param options - The options for the message.
 * @param options.isGlobal - Whether the message should be added to the global message list. As an example, when we are in a inner message provider like for todos and want to display a message in the global message list.
 * @returns The action to display the message.
 */
export const displayWarningMessage = (text: string, options?: { isGlobal?: boolean }): UtilActionType => {
  return displayMessage({ text: text, type: StyleEnums.DisplayType.Warning, isGlobal: options?.isGlobal });
};

export const closeMessage = (): UtilActionType => {
  return {
    type: ReduxConstants.UtilActions.CLOSE_MESSAGE,
  };
};

export const setMessageClass = (className: string): UtilActionType => {
  return {
    type: ReduxConstants.UtilActions.SET_MESSAGE_CLASS,
    payload: className,
  };
};

export const addToDismissMessage = (messageKey: SnackbarKey): UtilActionType => {
  return {
    type: ReduxConstants.UtilActions.ADD_TO_DISMISS_MESSAGE,
    payload: messageKey,
  };
};

export const clearToDismissMessages = (): UtilActionType => {
  return {
    type: ReduxConstants.UtilActions.CLEAR_TO_DISMISS_MESSAGES,
  };
};

export const displayDialog = (data: DialogProps): UtilActionType => {
  return {
    type: ReduxConstants.UtilActions.DISPLAY_DIALOG,
    payload: data,
  };
};

export const closeDialog = (id?: string): UtilActionType => {
  return {
    type: ReduxConstants.UtilActions.CLOSE_DIALOG,
    payload: id,
  };
};

export const asCompany = (companyId: number, action: { type: string; payload?: any }): UtilActionType => {
  return {
    type: ReduxConstants.UtilActions.AS_COMPANY,
    payload: {
      companyId,
      action,
    },
  };
};

export const setLeftNavCollapsed = (isCollapsed: boolean): UtilActionType => {
  return {
    type: ReduxConstants.UtilActions.SET_LEFT_NAV_COLLAPSED,
    payload: isCollapsed,
  };
};

export const setFullScreenDialogOpen = (isOpen: boolean, type?: "wizard"): UtilActionType => {
  return {
    type: ReduxConstants.UtilActions.SET_FULL_SCREEN_DIALOG_OPEN,
    payload: { isOpen: isOpen, type: type },
  };
};

export const setFilterDrawerOpen = (isOpen: boolean): UtilActionType => {
  return {
    type: ReduxConstants.UtilActions.SET_FILTER_DRAWER_OPEN,
    payload: isOpen,
  };
};
